/**
 * Itinerary Store
 *
 * Pinia store for itinerary CRUD operations and state management.
 * Uses the repository pattern for data access.
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { itineraryRepository } from '../repositories/itinerary.repository';
// =============================================================================
// CONSTANTS
// =============================================================================
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
// =============================================================================
// STORE
// =============================================================================
export const useItineraryStore = defineStore('itinerary', () => {
    // ---------------------------------------------------------------------------
    // STATE
    // ---------------------------------------------------------------------------
    const itineraries = ref([]);
    const selectedItinerary = ref(null);
    const isLoading = ref(false);
    const error = ref(null);
    const lastFetched = ref(null);
    // ---------------------------------------------------------------------------
    // GETTERS
    // ---------------------------------------------------------------------------
    /**
     * Find itinerary by ID
     */
    const itineraryById = computed(() => {
        return (id) => itineraries.value.find((i) => i.id === id);
    });
    /**
     * Filter itineraries by state
     */
    const itinerariesByState = computed(() => {
        return (state) => itineraries.value.filter((i) => i.state === state);
    });
    /**
     * Filter itineraries by customer
     */
    const itinerariesByCustomer = computed(() => {
        return (customerId) => itineraries.value.filter((i) => i.customer_id === customerId || i.customer?.id === customerId);
    });
    /**
     * Pending itineraries
     */
    const pendingItineraries = computed(() => itineraries.value.filter((i) => i.state === 'PENDING'));
    /**
     * Paid itineraries
     */
    const paidItineraries = computed(() => itineraries.value.filter((i) => i.state === 'PAID'));
    /**
     * Approved itineraries
     */
    const approvedItineraries = computed(() => itineraries.value.filter((i) => i.state === 'APPROVED'));
    /**
     * Expired itineraries
     */
    const expiredItineraries = computed(() => itineraries.value.filter((i) => i.state === 'EXPIRED'));
    /**
     * Cancelled itineraries
     */
    const cancelledItineraries = computed(() => itineraries.value.filter((i) => i.state === 'CANCELLED'));
    /**
     * Check if cache is still valid
     */
    const isCacheValid = computed(() => {
        if (!lastFetched.value)
            return false;
        return Date.now() - lastFetched.value.getTime() < CACHE_DURATION;
    });
    /**
     * Total count of itineraries
     */
    const totalItineraries = computed(() => itineraries.value.length);
    // ---------------------------------------------------------------------------
    // ACTIONS
    // ---------------------------------------------------------------------------
    /**
     * Fetch all itineraries
     */
    async function fetchAll(forceRefresh = false) {
        if (!forceRefresh && isCacheValid.value) {
            return itineraries.value;
        }
        isLoading.value = true;
        error.value = null;
        try {
            const data = await itineraryRepository.getAll();
            itineraries.value = data;
            lastFetched.value = new Date();
            return data;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : 'Error fetching itineraries';
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    /**
     * Fetch single itinerary by ID
     */
    async function fetchById(id) {
        isLoading.value = true;
        error.value = null;
        try {
            const data = await itineraryRepository.getById(id);
            selectedItinerary.value = data;
            // Update in list if exists
            const index = itineraries.value.findIndex((i) => i.id === id);
            if (index !== -1) {
                itineraries.value[index] = data;
            }
            return data;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : 'Error fetching itinerary';
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    /**
     * Create new itinerary
     */
    async function create(data) {
        isLoading.value = true;
        error.value = null;
        try {
            const newItinerary = await itineraryRepository.create(data);
            itineraries.value.push(newItinerary);
            selectedItinerary.value = newItinerary;
            return newItinerary;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : 'Error creating itinerary';
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    /**
     * Update existing itinerary
     */
    async function update(id, data) {
        isLoading.value = true;
        error.value = null;
        try {
            const updatedItinerary = await itineraryRepository.update(id, data);
            // Update in list
            const index = itineraries.value.findIndex((i) => i.id === id);
            if (index !== -1) {
                itineraries.value[index] = updatedItinerary;
            }
            // Update selected if same
            if (selectedItinerary.value?.id === id) {
                selectedItinerary.value = updatedItinerary;
            }
            return updatedItinerary;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : 'Error updating itinerary';
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    /**
     * Delete itinerary
     */
    async function remove(id) {
        isLoading.value = true;
        error.value = null;
        try {
            await itineraryRepository.delete(id);
            itineraries.value = itineraries.value.filter((i) => i.id !== id);
            if (selectedItinerary.value?.id === id) {
                selectedItinerary.value = null;
            }
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : 'Error deleting itinerary';
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    /**
     * Update itinerary state only
     */
    async function updateState(id, state) {
        isLoading.value = true;
        error.value = null;
        try {
            const updatedItinerary = await itineraryRepository.updateState(id, state);
            // Update in list
            const index = itineraries.value.findIndex((i) => i.id === id);
            if (index !== -1) {
                itineraries.value[index] = updatedItinerary;
            }
            // Update selected if same
            if (selectedItinerary.value?.id === id) {
                selectedItinerary.value = updatedItinerary;
            }
            return updatedItinerary;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : 'Error updating itinerary status';
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    /**
     * @deprecated Use updateState.
     */
    async function updateStatus(id, state) {
        return updateState(id, state);
    }
    /**
     * Set selected itinerary
     */
    function setSelected(itinerary) {
        selectedItinerary.value = itinerary;
    }
    /**
     * Clear selected itinerary
     */
    function clearSelected() {
        selectedItinerary.value = null;
    }
    /**
     * Invalidate cache
     */
    function invalidateCache() {
        lastFetched.value = null;
    }
    /**
     * Reset store state
     */
    function $reset() {
        itineraries.value = [];
        selectedItinerary.value = null;
        isLoading.value = false;
        error.value = null;
        lastFetched.value = null;
    }
    // ---------------------------------------------------------------------------
    // RETURN
    // ---------------------------------------------------------------------------
    return {
        // State
        itineraries,
        selectedItinerary,
        isLoading,
        error,
        lastFetched,
        // Getters
        itineraryById,
        itinerariesByState,
        itinerariesByCustomer,
        pendingItineraries,
        paidItineraries,
        approvedItineraries,
        expiredItineraries,
        cancelledItineraries,
        isCacheValid,
        totalItineraries,
        // Actions
        fetchAll,
        fetchById,
        create,
        update,
        remove,
        updateState,
        updateStatus,
        setSelected,
        clearSelected,
        invalidateCache,
        $reset,
    };
});
